'use client';

import { useState, useImperativeHandle, forwardRef, useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { motion } from 'framer-motion';
import { formatBusinessDescription } from '@/lib/utils';

interface CampaignBusinessDescriptionProps {
  businessDescription: string;
  businessUrl: string;
  setBusinessDescription: (description: string) => void;
  setStepValid: (valid: boolean) => void;
}

export interface CampaignBusinessDescriptionRef {
  approveDescription: () => void;
}

// The description a person enters or corrects when Glitch could not read their website. Every edit reaches the
// parent as it is typed, so whatever submits the step reads the text on screen: the description used to reach the
// parent only on approval, in the same click that submitted, so the submit read the one from before (glitchads#7065).
// It is saved as the business's own description; nothing writes it to the scraped page, which every organization
// with the same website reads.
export const CampaignBusinessDescription = forwardRef<CampaignBusinessDescriptionRef, CampaignBusinessDescriptionProps>(({
  businessDescription,
  businessUrl,
  setBusinessDescription,
  setStepValid,
}, ref) => {
  const posthog = usePostHog();
  const [description, setDescription] = useState<string>(formatBusinessDescription(businessDescription));
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const originalDescriptionRef = useRef(businessDescription);
  const hasTrackedEdit = useRef(false);

  const approveDescription = () => {
    posthog?.capture('onboarding_business_description_approved', {
      description_length: description.length,
      was_edited: description !== originalDescriptionRef.current,
      original_length: originalDescriptionRef.current?.length || 0,
      business_url: businessUrl,
    });
  };

  useImperativeHandle(ref, () => ({
    approveDescription
  }));

  // The parent starts from the formatted text shown here, and the step may always continue. Run once: these setters
  // are not stable, and listing them looped.
  useEffect(() => {
    setBusinessDescription(description);
    setStepValid(true);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const newDescription = e.target.value;
    setDescription(newDescription);
    setBusinessDescription(newDescription);

    // Track first edit if the description differs from the original
    if (!hasTrackedEdit.current && newDescription !== originalDescriptionRef.current) {
      posthog?.capture('onboarding_business_description_edited', {
        original_length: originalDescriptionRef.current?.length || 0,
        new_length: newDescription.length,
        business_url: businessUrl,
      });
      hasTrackedEdit.current = true;
    }
  };

  return (
    <motion.div
      className='campaign-goal-generate flex-row space-y-6 !mt-0'
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
    >
      <motion.p
        className='text-base text-slate-900 mb-4'
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        Please <strong>review and edit</strong> the following summary to ensure you are happy with it. It will be used to generate keywords for your <strong>Prospecting</strong> campaign.
      </motion.p>
      <motion.div
        className='space-y-4'
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        onAnimationComplete={() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
          }
        }}
      >
        <Card>
          <Textarea
            ref={textareaRef}
            name={'description'}
            className='h-[330px] p-4 !text-base leading-relaxed text-slate-900'
            defaultValue={description}
            onChange={handleChange}
          />
        </Card>
      </motion.div>
    </motion.div>
  );
});
